import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { first } from 'rxjs';
import { UserDetails } from '@bn/api-interfaces';
import { FeatureFlagDirective, UserPermissionsDirective } from '@bn/auth/directives';
import { MENU_ACCESS } from '@bn/auth/tokens/menu-access.token';
import { MenuItem } from '../models';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';

@Component({
  standalone: true,
  selector: 'bn-layout-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  imports: [NgClass, NgTemplateOutlet, RouterLink, RouterLinkActive, FaIconComponent, FeatureFlagDirective, UserPermissionsDirective, BadgeComponent],
})
export class MainMenuComponent implements OnInit {
  @Input() menuConfig!: () => MenuItem[];
  @Input() childRoutes!: () => Record<string, string>;
  @Input() defaultOpenSections: string[] = [];
  @Input() homeRoute = '/';

  public menuItems: MenuItem[] = [];

  private readonly router = inject(Router);
  private readonly access = inject(MENU_ACCESS, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly openMenuItems = new Map<string, boolean>();

  public ngOnInit(): void {
    if (this.access) {
      this.access
        .getFeatureFlags()
        .pipe(first())
        .subscribe((featureFlags) => this.handleFeatureFlagLogic(featureFlags));
    } else {
      this.menuItems = this.menuConfig();
    }

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!(event instanceof NavigationEnd)) return;

      const baseRoute = window.location.pathname.substring(1)?.split('/')?.at(0) || this.homeRoute;

      switch (true) {
        case baseRoute === this.homeRoute:
          this.defaultOpenSections.forEach((section) => {
            if (this.openMenuItems.get(section) !== false) this.openMenuItems.set(section, true);
          });
          break;
        case this.childRoutes()[baseRoute] === undefined:
          this.defaultOpenSections.forEach((section) => {
            this.openMenuItems.set(section, true);
          });
          break;
        case this.childRoutes()[baseRoute] !== undefined:
          this.openMenuItems.set(this.childRoutes()[baseRoute], true);
          break;
        default:
          return;
      }

      this.syncMenuOpenState();
    });
  }

  public onMenuItemClick(menuItem: MenuItem, isChildItem: boolean): void {
    const itemId = this.childRoutes()[menuItem.route ?? ''] ?? menuItem.name;
    if (!isChildItem) this.openMenuItems.set(itemId, !this.openMenuItems.get(itemId));
    this.syncMenuOpenState();
  }

  private handleFeatureFlagLogic(featureFlags: UserDetails['FeatureFlags']): void {
    this.menuItems = this.menuConfig()
      .map((menuItem) => {
        if (!menuItem.children || menuItem.isIndependentRootItem) return menuItem;

        const filteredChildren = menuItem.children.filter((child) => {
          if (child.negatedFeatureFlag && featureFlags.includes(child.negatedFeatureFlag)) return false;
          if (child.negatedFeatureFlag && !featureFlags.includes(child.negatedFeatureFlag)) return true;
          if (!child.featureFlag && !child.negatedFeatureFlag) return true;
          return !!(child.featureFlag && featureFlags.includes(child.featureFlag));
        });

        if (filteredChildren.length === 0) return null;

        return { ...menuItem, children: filteredChildren };
      })
      .filter((menuItem) => menuItem !== null);
  }

  private syncMenuOpenState(): void {
    this.menuItems = this.menuItems.map((item) => ({
      ...item,
      isOpen: this.openMenuItems.get(item.name) === true,
    }));
  }
}
